import PagedResourceSelect from '@app/components/PagedResourceSelect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Drawer from '@app/components/Drawer';
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import StatusToast, { type StatusToastTone } from '@app/components/StatusToast';
import KpiCard from '@app/components/KpiCard';
import ListWorkspace, { type ListWorkspaceColumn } from '@app/components/ListWorkspace';
import { EyeIcon, RadarIcon, TrashIcon } from '@app/components/icons';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useApiConfiguration } from '@app/hooks/useApiConfiguration';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useSitesApi } from '@app/hooks/useApis';
import { useCursorPagination } from '@app/hooks/useCursorPagination';
import type { Device, SitesListResponse } from '@app/types/sdk';
import { ApiResponseError, buildOrgUrl, cloneHeadersWithAuth } from '@app/utils/api';
import { formatDateTime, formatNumber, formatRelativeTime } from '@app/utils/format';
import {
  EndpointDetailContent,
  formatMacAddress,
  normalizeEndpointDetailResponse,
  normalizeEndpointListResponse,
  type EndpointAction,
  type EndpointListResponse,
  type EndpointRecord
} from './endpointShared';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';

export { normalizeEndpointListResponse } from './endpointShared';

interface EndpointFilters {
  q: string;
  siteId: string;
  switchDeviceId: string;
  hasIp: 'all' | 'with' | 'without';
  freshness: 'all' | 'active' | 'stale';
  sort: 'recent' | 'oldest';
}

const DEFAULT_FILTERS: EndpointFilters = {
  q: '',
  siteId: '',
  switchDeviceId: '',
  hasIp: 'all',
  freshness: 'all',
  sort: 'recent'
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const STALE_WINDOW_MS = 24 * 60 * 60 * 1000;

const readString = (value: unknown): string => (typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim());

const readObject = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const isStale = (lastSeenAt: string): boolean => {
  if (!lastSeenAt) {
    return true;
  }
  const seen = new Date(lastSeenAt);
  if (Number.isNaN(seen.getTime())) {
    return true;
  }
  return Date.now() - seen.getTime() > STALE_WINDOW_MS;
};

const formatBadgeClass = (tone: 'success' | 'warning' | 'ghost') => `ui-badge ui-badge--${tone}`;


const readAttachment = (record: EndpointRecord): Record<string, unknown> | null => readObject(record.attrs?.attachment);

const readResolvedAttachment = (record: EndpointRecord): Record<string, unknown> | null => readObject(readAttachment(record)?.resolved_to);

const attachmentDeviceId = (record: EndpointRecord): string => readString(readResolvedAttachment(record)?.device_id);

const attachmentDeviceName = (record: EndpointRecord): string => readString(readResolvedAttachment(record)?.device_name);

const attachmentDisplayName = (record: EndpointRecord, deviceNames: Record<string, string>): string => {
  const deviceId = attachmentDeviceId(record);
  const deviceName = attachmentDeviceName(record);
  return deviceName || (deviceId ? deviceNames[deviceId] ?? deviceId : '');
};

const observingSwitchDisplayName = (record: EndpointRecord, deviceNames: Record<string, string>): string =>
  deviceNames[record.switchDeviceId] ?? record.switchDeviceId ?? '';

const attachmentSecondaryText = (record: EndpointRecord, deviceNames: Record<string, string>): string => {
  const observedOn = observingSwitchDisplayName(record, deviceNames) || '—';
  const portText = record.switchPort || '—';
  const vlanText = record.vlan ? ` • VLAN ${record.vlan}` : '';
  if (attachmentDisplayName(record, deviceNames)) {
    return `Observed on ${observedOn} • ${portText}${vlanText}`;
  }
  return `${observedOn} • ${portText}${vlanText}`;
};

const EndpointsPage = () => {
  const orgId = useActiveOrgId();
  const apiConfig = useApiConfiguration();
  const sitesApi = useSitesApi();
  const basePath = apiConfig.basePath ?? '';
  const { fetchWithAuthRetry } = useAuthFetch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const siteIdFromQuery = searchParams.get('siteId')?.trim() ?? '';
  const switchDeviceIdFromQuery = searchParams.get('switchDeviceId')?.trim() ?? '';
  const endpointIdFromQuery = searchParams.get('endpointId')?.trim() ?? '';
  const [filters, setFilters] = useState<EndpointFilters>(() => ({
    ...DEFAULT_FILTERS,
    siteId: siteIdFromQuery,
    switchDeviceId: switchDeviceIdFromQuery
  }));
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [selectedEndpointId, setSelectedEndpointId] = useState<string | null>(null);
  const [selectedEndpointDetail, setSelectedEndpointDetail] = useState<EndpointRecord | null>(null);
  const [selectedEndpointLoading, setSelectedEndpointLoading] = useState(false);
  const [selectedEndpointError, setSelectedEndpointError] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [pendingDeleteRows, setPendingDeleteRows] = useState<EndpointRecord[] | null>(null);
  const [locallyDeletedIds, setLocallyDeletedIds] = useState<Set<string>>(() => new Set());
  const [deleteToast, setDeleteToast] = useState<{ tone: StatusToastTone; message: string } | null>(null);
  const [actionErrorMessage, setActionErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setLocallyDeletedIds(new Set());
  }, [orgId]);

  const {
    state: { page, pageSize, cursor },
    setPage,
    setPageSize,
    reset: resetPagination,
    registerPageMeta,
    deriveMeta
  } = useCursorPagination({ initialPageSize: PAGE_SIZE_OPTIONS[0], pageSizeOptions: PAGE_SIZE_OPTIONS });

  const endpointFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    return async () => {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      if (cursor.direction === 'after' && cursor.value) {
        params.set('after', cursor.value);
      }
      if (cursor.direction === 'before' && cursor.value) {
        params.set('before', cursor.value);
      }
      if (filters.q.trim()) {
        params.set('q', filters.q.trim());
      }
      if (filters.siteId.trim()) {
        params.set('siteId', filters.siteId.trim());
      }
      if (filters.switchDeviceId.trim()) {
        params.set('switchDeviceId', filters.switchDeviceId.trim());
      }
      if (filters.hasIp !== 'all') {
        params.set('hasIp', filters.hasIp === 'with' ? 'true' : 'false');
      }
      if (filters.freshness !== 'all') {
        params.set('freshness', filters.freshness);
      }
      params.set('sort', filters.sort === 'oldest' ? 'lastSeenAt' : '-lastSeenAt');

      const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
      const response = await fetch(buildOrgUrl(basePath, orgId, `endpoints?${params.toString()}`), {
        method: 'GET',
        headers,
        cache: 'no-store'
      });
      if (!response.ok) {
        throw new ApiResponseError(response, `Request failed with status ${response.status}`);
      }
      const payload = normalizeEndpointListResponse(await response.json());
      const linkHeader = response.headers.get('Link');
      return { ...payload, next: payload.next ?? undefined, prev: payload.prev ?? undefined, linkHeader };
    };
  }, [basePath, cursor, filters, orgId, pageSize, refreshIndex]);

  const { data, loading, error, backendMissing } = useApiQuery<(EndpointListResponse & { linkHeader?: string | null }) | null>(endpointFetcher, [endpointFetcher]);

  useEffect(() => {
    if (!data) {
      return;
    }
    registerPageMeta({ next: data.next, prev: data.prev, exactTotal: data.exactTotal }, data.linkHeader);
  }, [data, registerPageMeta]);

  const rows = useMemo(() => (data?.items ?? []).filter((row) => !locallyDeletedIds.has(row.id)), [data, locallyDeletedIds]);
  const paginationMeta = useMemo(() => deriveMeta(rows.length), [deriveMeta, rows.length]);

  const sitesFetcher = useMemo(
    () => (orgId ? () => sitesApi.orgsOrgIdSitesGet({ orgId, limit: 200 }, { cache: 'no-store' }) : null),
    [orgId, sitesApi]
  );
  const sitesQuery = useApiQuery<SitesListResponse>(sitesFetcher, [sitesFetcher]);

  const devicesFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    return async () => {
      const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
      const response = await fetch(buildOrgUrl(basePath, orgId, 'devices?limit=200&sort=displayName'), {
        method: 'GET',
        headers,
        cache: 'no-store'
      });
      if (!response.ok) {
        throw new ApiResponseError(response, `Request failed with status ${response.status}`);
      }
      const payload = (await response.json()) as { items?: Device[] };
      return payload.items ?? [];
    };
  }, [basePath, orgId]);
  const devicesQuery = useApiQuery<Device[]>(devicesFetcher, [devicesFetcher]);

  const siteOptions = useMemo(() => sitesQuery.data?.items ?? [], [sitesQuery.data]);
  const siteNames = useMemo(() => {
    const lookup: Record<string, string> = {};
    for (const site of siteOptions) {
      if (site.id) {
        lookup[site.id] = site.name ?? site.id;
      }
    }
    return lookup;
  }, [siteOptions]);

  const deviceNames = useMemo(() => {
    const lookup: Record<string, string> = {};
    for (const device of devicesQuery.data ?? []) {
      if (device.id) {
        lookup[device.id] = device.displayName ?? device.hostname ?? device.mgmtAddress ?? device.id;
      }
    }
    return lookup;
  }, [devicesQuery.data]);

  const switchOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: Array<{ id: string; name: string }> = [];
    for (const row of rows) {
      if (!row.switchDeviceId || seen.has(row.switchDeviceId)) {
        continue;
      }
      seen.add(row.switchDeviceId);
      options.push({ id: row.switchDeviceId, name: deviceNames[row.switchDeviceId] ?? row.switchDeviceId });
    }
    options.sort((a, b) => a.name.localeCompare(b.name));
    return options;
  }, [deviceNames, rows]);

  const selectedEndpointFromRows = useMemo(
    () => rows.find((row) => row.id === selectedEndpointId || row.endpointKey === selectedEndpointId) ?? null,
    [rows, selectedEndpointId]
  );
  useEffect(() => {
    const normalizedEndpointId = endpointIdFromQuery || null;
    setSelectedEndpointId((prev) => (prev === normalizedEndpointId ? prev : normalizedEndpointId));
  }, [endpointIdFromQuery]);

  const updateSelectedEndpointId = useCallback(
    (endpointId: string | null) => {
      setSelectedEndpointId(endpointId);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (endpointId) {
          next.set('endpointId', endpointId);
        } else {
          next.delete('endpointId');
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!selectedEndpointId) {
      setSelectedEndpointDetail(null);
      setSelectedEndpointLoading(false);
      setSelectedEndpointError(null);
      return;
    }
    if (selectedEndpointFromRows) {
      setSelectedEndpointDetail(selectedEndpointFromRows);
      setSelectedEndpointLoading(false);
      setSelectedEndpointError(null);
      return;
    }
    if (!orgId) {
      setSelectedEndpointDetail(null);
      setSelectedEndpointLoading(false);
      setSelectedEndpointError('Select an organization to load endpoint details.');
      return;
    }

    let cancelled = false;
    const loadSelectedEndpoint = async () => {
      setSelectedEndpointLoading(true);
      setSelectedEndpointError(null);
      try {
        const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
        const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, `endpoints/${encodeURIComponent(selectedEndpointId)}`), {
          method: 'GET',
          headers,
          cache: 'no-store'
        });
        if (!response.ok) {
          throw new ApiResponseError(response, `Failed to load endpoint ${selectedEndpointId}`);
        }
        const payload = normalizeEndpointDetailResponse(await response.json());
        if (!payload) {
          throw new Error(`Endpoint ${selectedEndpointId} was not found.`);
        }
        if (cancelled) {
          return;
        }
        setSelectedEndpointDetail(payload);
        setSelectedEndpointLoading(false);
      } catch (caught) {
        if (cancelled) {
          return;
        }
        setSelectedEndpointDetail(null);
        setSelectedEndpointLoading(false);
        setSelectedEndpointError(caught instanceof Error ? caught.message : 'Failed to load endpoint details.');
      }
    };

    void loadSelectedEndpoint();
    return () => {
      cancelled = true;
    };
  }, [basePath, endpointIdFromQuery, fetchWithAuthRetry, orgId, selectedEndpointFromRows, selectedEndpointId]);

  const updateFilters = (patch: Partial<EndpointFilters>) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      const nextParams = new URLSearchParams(searchParams);
      if (next.siteId.trim()) {
        nextParams.set('siteId', next.siteId.trim());
      } else {
        nextParams.delete('siteId');
      }
      if (next.switchDeviceId.trim()) {
        nextParams.set('switchDeviceId', next.switchDeviceId.trim());
      } else {
        nextParams.delete('switchDeviceId');
      }
      setSearchParams(nextParams, { replace: true });
      return next;
    });
    resetPagination();
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('siteId');
      next.delete('switchDeviceId');
      return next;
    }, { replace: true });
    resetPagination();
  };

  const handleRefresh = () => setRefreshIndex((prev) => prev + 1);

  const handleDeleteEndpoints = useCallback(
    (rowsToDelete: EndpointRecord[]) => {
      if (!orgId || rowsToDelete.length === 0 || deleteBusy) {
        return;
      }
      const uniqueRows = rowsToDelete.filter((row, index, source) => row.id && source.findIndex((candidate) => candidate.id === row.id) === index);
      if (uniqueRows.length === 0) {
        return;
      }
      setPendingDeleteRows(uniqueRows);
    },
    [deleteBusy, orgId]
  );

  const confirmDeleteEndpoints = useCallback(
    async () => {
      const uniqueRows = pendingDeleteRows;
      if (!orgId || !uniqueRows || uniqueRows.length === 0 || deleteBusy) {
        return;
      }

      setActionErrorMessage(null);
      setDeleteBusy(true);

      const deletedIds = new Set<string>();
      let failureMessage: string | null = null;

      try {
        await Promise.allSettled(
          uniqueRows.map(async (row) => {
            const endpointId = row.id;
            const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
            try {
              const getResponse = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, `endpoints/${encodeURIComponent(endpointId)}`), {
                method: 'GET',
                headers,
                cache: 'no-store'
              });
              if (!getResponse.ok) {
                throw new ApiResponseError(getResponse, `Failed to load endpoint ${endpointId}`);
              }
              const etag = getResponse.headers.get('ETag') ?? undefined;
              const deleteHeaders = await cloneHeadersWithAuth({});
              if (etag) {
                deleteHeaders['If-Match'] = etag;
              }
              const deleteResponse = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, `endpoints/${encodeURIComponent(endpointId)}`), {
                method: 'DELETE',
                headers: deleteHeaders
              });
              if (!deleteResponse.ok) {
                throw new ApiResponseError(deleteResponse, `Failed to delete endpoint ${endpointId}`);
              }
              deletedIds.add(endpointId);
            } catch (caught) {
              failureMessage = caught instanceof Error ? caught.message : `Failed to delete endpoint ${endpointId}`;
              console.warn('[endpoints] Failed to delete endpoint', caught);
            }
          })
        );

        if (selectedEndpointId && deletedIds.has(selectedEndpointId)) {
          updateSelectedEndpointId(null);
        }

        if (deletedIds.size > 0) {
          setLocallyDeletedIds((current) => new Set([...current, ...deletedIds]));
          setPendingDeleteRows(null);
          resetPagination();
          setRefreshIndex((prev) => prev + 1);
        }

        if (failureMessage) {
          const message = deletedIds.size > 0
            ? `${deletedIds.size} of ${uniqueRows.length} endpoints deleted. ${uniqueRows.length - deletedIds.size} could not be deleted.`
            : failureMessage;
          setActionErrorMessage(message);
          setDeleteToast({ tone: deletedIds.size > 0 ? 'warning' : 'error', message });
        } else {
          setDeleteToast({
            tone: 'success',
            message: `${deletedIds.size} endpoint${deletedIds.size === 1 ? '' : 's'} deleted successfully.`
          });
        }
      } finally {
        setDeleteBusy(false);
      }
    },
    [basePath, deleteBusy, fetchWithAuthRetry, orgId, pendingDeleteRows, resetPagination, selectedEndpointId, updateSelectedEndpointId]
  );

  const openAction = (action: EndpointAction | null) => {
    if (!action || action.disabled || !action.href) {
      return;
    }
    if (/^https?:\/\//i.test(action.href)) {
      window.open(action.href, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(action.href);
  };

  const filtersActive = Boolean(
    filters.q.trim().length ||
      filters.siteId.trim().length ||
      filters.switchDeviceId.trim().length ||
      filters.hasIp !== 'all' ||
      filters.freshness !== 'all' ||
      filters.sort !== 'recent'
  );

  const topSlot = (
    <div className="kpi-grid">
      <KpiCard label="Visible endpoints" value={loading ? '…' : formatNumber(rows.length)} secondary="Current page" />
      <KpiCard
        label="With IP address"
        value={loading ? '…' : formatNumber(rows.filter((row) => row.ipAddress).length)}
        secondary={loading ? '…' : `${rows.filter((row) => !row.ipAddress).length} MAC-only`}
      />
      <KpiCard label="Stale" value={loading ? '…' : formatNumber(rows.filter((row) => isStale(row.lastSeenAt)).length)} secondary="Last seen > 24h" />
      <KpiCard
        label="Observing switches"
        value={loading ? '…' : formatNumber(new Set(rows.map((row) => row.switchDeviceId).filter(Boolean)).size)}
        secondary={loading ? '…' : `${new Set(rows.map((row) => row.siteId).filter(Boolean)).size} sites`}
      />
    </div>
  );

  const columns: ListWorkspaceColumn<EndpointRecord>[] = [
    {
      key: 'endpoint',
      header: 'Endpoint',
      width: '20%',
      render: (row) => (
        <div className="endpoint-cell">
          <strong>{formatMacAddress(row.macAddress)}</strong>
        </div>
      )
    },
    {
      key: 'ip',
      header: 'IP address',
      width: '13%',
      render: (row) =>
        row.ipAddress ? <span className={formatBadgeClass('success')}>{row.ipAddress}</span> : <span className={formatBadgeClass('ghost')}>MAC only</span>
    },
    {
      key: 'classification',
      header: 'Classification',
      width: '12%',
      render: (row) => {
        const classification = readObject(row.attrs?.['classification']);
        const classificationType = readString(classification?.['type']);
        return <span className={formatBadgeClass(classificationType ? 'success' : 'ghost')}>{classificationType || 'Unclassified'}</span>;
      }
    },
    {
      key: 'attachment',
      header: 'Attachment',
      width: '22%',
      render: (row) => {
        const attachedTo = attachmentDisplayName(row, deviceNames);
        return (
          <div className="endpoint-cell">
            <strong>{attachedTo || observingSwitchDisplayName(row, deviceNames) || '—'}</strong>
            <span>{attachmentSecondaryText(row, deviceNames)}</span>
          </div>
        );
      }
    },
    {
      key: 'site',
      header: 'Site',
      width: '13%',
      render: (row) => siteNames[row.siteId] ?? row.siteId ?? '—'
    },
    {
      key: 'seen',
      header: 'Last seen',
      width: '12%',
      render: (row) => (
        <div className="endpoint-cell">
          <strong>{formatRelativeTime(row.lastSeenAt)}</strong>
          <span>{formatDateTime(row.lastSeenAt)}</span>
        </div>
      )
    },
    {
      key: 'version',
      header: 'State',
      width: '8%',
      align: 'center',
      render: (row) => (
        <div className="endpoint-state">
          <span className={formatBadgeClass(isStale(row.lastSeenAt) ? 'warning' : 'success')}>{isStale(row.lastSeenAt) ? 'stale' : 'active'}</span>
          <span className="endpoint-state__version">v{row.version}</span>
        </div>
      )
    }
  ];

  const rowActions = (row: EndpointRecord) => (
    <>
      <button
        type="button"
        className="list-workspace__icon-button"
        aria-label="Inspect endpoint"
        title="Inspect endpoint"
        onClick={(event) => {
          event.stopPropagation();
          updateSelectedEndpointId(row.id);
        }}
        disabled={deleteBusy}
      >
        <EyeIcon size={16} />
      </button>
      <button
        type="button"
        className="list-workspace__icon-button is-danger"
        aria-label="Delete endpoint"
        title="Delete endpoint"
        disabled={deleteBusy}
        onClick={(event) => {
          event.stopPropagation();
          void handleDeleteEndpoints([row]);
        }}
      >
        <TrashIcon size={16} />
      </button>
    </>
  );

  const filterBar = (
    <div className="list-workspace__filter-grid">
      <div className="form-grid form-grid--two">
        <label className="form-field">
          <span className="form-field__label">Search</span>
          <input type="text" className="input" value={filters.q} onChange={(event) => updateFilters({ q: event.target.value })} placeholder="Search MAC, IP, port, or device" />
        </label>
        <label className="form-field">
          <span className="form-field__label">Site</span>
          <PagedResourceSelect resource="sites" value={filters.siteId} allLabel="All sites" onChange={(siteId) => updateFilters({ siteId })} />
        </label>
        <label className="form-field">
          <span className="form-field__label">Observing switch</span>
          <PagedResourceSelect resource="devices" deviceRole="switch" value={filters.switchDeviceId} allLabel="All switches" onChange={(switchDeviceId) => updateFilters({ switchDeviceId })} />
        </label>
        <label className="form-field">
          <span className="form-field__label">IP enrichment</span>
          <select className="select" value={filters.hasIp} onChange={(event) => updateFilters({ hasIp: event.target.value as EndpointFilters['hasIp'] })}>
            <option value="all">All endpoints</option>
            <option value="with">With IP</option>
            <option value="without">MAC only</option>
          </select>
        </label>
        <label className="form-field">
          <span className="form-field__label">Freshness</span>
          <select className="select" value={filters.freshness} onChange={(event) => updateFilters({ freshness: event.target.value as EndpointFilters['freshness'] })}>
            <option value="all">Any age</option>
            <option value="active">Seen in 24h</option>
            <option value="stale">Older than 24h</option>
          </select>
        </label>
        <label className="form-field">
          <span className="form-field__label">Sort</span>
          <select className="select" value={filters.sort} onChange={(event) => updateFilters({ sort: event.target.value as EndpointFilters['sort'] })}>
            <option value="recent">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </label>
      </div>
    </div>
  );

  if (!orgId) {
    return (
      <OrgModuleLayout title="Endpoints" description="Org-wide endpoint attachment and enrichment view." topSlot={topSlot}>
        <OrgModulePlaceholder intent="Select an organization to load endpoints." />
      </OrgModuleLayout>
    );
  }

  if (backendMissing) {
    return (
      <OrgModuleLayout title="Endpoints" description="Org-wide endpoint attachment and enrichment view." topSlot={topSlot}>
        <OrgModulePlaceholder intent="Implement the Endpoints API in the backend to populate endpoint state and history." />
      </OrgModuleLayout>
    );
  }

  return (
    <OrgModuleLayout
      title="Endpoints"
      description={
        <span className="endpoint-page__title">
          <RadarIcon size={18} />
          Org-wide endpoint attachment and enrichment view.
        </span>
      }
      topSlot={topSlot}
    >
      <ListWorkspace
        headerLabel="Endpoint attachments"
        rows={rows}
        columns={columns}
        rowId={(row) => row.id}
        onRowClick={(row) => updateSelectedEndpointId(row.id)}
        highlightRowId={selectedEndpointId}
        loading={loading}
        headerStatus={
          error || actionErrorMessage ? (
            <div className="list-workspace__status">
              {error ? `Failed to load endpoints: ${error.message}` : actionErrorMessage}
            </div>
          ) : undefined
        }
        rowActions={rowActions}
        onDeleteSelected={handleDeleteEndpoints}
        filterBar={filterBar}
        filterActive={filtersActive}
        onFilterReset={handleResetFilters}
        onRefresh={handleRefresh}
        page={page}
        pageSize={pageSize}
        paginationMeta={paginationMeta}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        emptyState={
          <div className="empty-hero" style={{ textAlign: 'left' }}>
            <h3 className="empty-hero__title">No endpoints found</h3>
            <p className="empty-hero__body">Adjust the filters or wait for endpoint reconciliation to publish the latest site-scoped attachment data.</p>
          </div>
        }
      />

      <Drawer
        open={Boolean(selectedEndpointId)}
        onClose={() => updateSelectedEndpointId(null)}
        title={selectedEndpointDetail ? formatMacAddress(selectedEndpointDetail.macAddress) : 'Endpoint'}
        subtitle={selectedEndpointDetail?.ipAddress || undefined}
        footer={
          selectedEndpointDetail ? (
            <div className="button-row">
              {selectedEndpointDetail.actions.map((action) => (
                <button
                  key={`${action.label}-${action.href}`}
                  type="button"
                  className="ui-button ui-button--ghost"
                  disabled={action.disabled || !action.href}
                  onClick={() => openAction(action)}
                >
                  {action.label}
                </button>
              ))}
              <button
                type="button"
                className="ui-button ui-button--danger"
                onClick={() => {
                  void handleDeleteEndpoints([selectedEndpointDetail]);
                }}
                disabled={deleteBusy}
              >
                {deleteBusy ? 'Deleting…' : 'Delete endpoint'}
              </button>
            </div>
          ) : undefined
        }
      >
        {selectedEndpointLoading ? <p>Loading endpoint…</p> : null}
        {!selectedEndpointLoading && selectedEndpointError ? <OrgModulePlaceholder intent={selectedEndpointError} /> : null}
        {!selectedEndpointLoading && selectedEndpointDetail ? <EndpointDetailContent endpoint={selectedEndpointDetail} /> : null}
      </Drawer>
      <DeleteConfirmationModal
        open={pendingDeleteRows != null}
        title={pendingDeleteRows?.length === 1 ? 'Delete endpoint?' : `Delete ${pendingDeleteRows?.length ?? 0} endpoints?`}
        description={
          pendingDeleteRows?.length === 1
            ? `Delete endpoint ${formatMacAddress(pendingDeleteRows[0].macAddress)}? This cannot be undone.`
            : 'This permanently removes the selected endpoints.'
        }
        deleting={deleteBusy}
        onCancel={() => setPendingDeleteRows(null)}
        onConfirm={() => void confirmDeleteEndpoints()}
      />
      {deleteToast && <StatusToast tone={deleteToast.tone} message={deleteToast.message} occurrence={deleteToast} onDismiss={() => setDeleteToast(null)} />}
    </OrgModuleLayout>
  );
};

export default EndpointsPage;
